import { CSSInterpolation, styled, Theme, ToggleButtonGroup, ToggleButtonGroupProps, useThemeProps } from '@mui/material'
import clsx from 'clsx'
import isNull from 'lodash/isNull'
import { MouseEvent } from 'react'

export type SegmentedControlProps<TValue extends string = string> = Omit<
  ToggleButtonGroupProps,
  'size' | 'exclusive' | 'orientation' | 'onChange' | 'value'
> & {
  value?: TValue
  onChange?: (event: MouseEvent<HTMLElement>, value: TValue) => void
}

type OwnerState = Record<string, never>

type SlotStyle = CSSInterpolation | ((arg: { theme: Theme; ownerState: OwnerState }) => CSSInterpolation)

export type OmuiSegmentedControlComponent = {
  defaultProps?: Partial<SegmentedControlProps>
  styleOverrides?: {
    root?: SlotStyle
  }
  variants?: Array<{
    props: Partial<OwnerState>
    style: SlotStyle
  }>
}

declare module '@mui/material/styles' {
  interface Components {
    OmuiSegmentedControl?: OmuiSegmentedControlComponent
  }

  interface ComponentsPropsList {
    OmuiSegmentedControl: Partial<OwnerState>
  }
}

export const omuiSegmentedControlClasses = {
  root: 'OmuiSegmentedControl-root',
} as const

const SegmentedControlRoot = styled(ToggleButtonGroup, {
  name: 'OmuiSegmentedControl',
  slot: 'Root',
  overridesResolver: (_props, styles) => styles.root,
})<{ ownerState: OwnerState }>({})

export const SegmentedControl = <TValue extends string = string>(inProps: SegmentedControlProps<TValue>) => {
  const props = useThemeProps({ props: inProps, name: 'OmuiSegmentedControl' })
  const { onChange, className, ...rest } = props
  const ownerState: OwnerState = {}

  return (
    <SegmentedControlRoot
      {...rest}
      ownerState={ownerState}
      className={clsx(omuiSegmentedControlClasses.root, className)}
      size="medium"
      exclusive
      onChange={(event, newValue) => {
        // newValue can be null if the user clicks on the same button twice
        // this can cause unexpected behaviour if the onChange handler is not expecting it
        if (!isNull(newValue) && onChange) {
          onChange(event, newValue)
        }
      }}
    />
  )
}
